/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { TrendingUp, BarChart3, Wallet, Gift, Package } from "lucide-react";
import { CoverMedia, coverSlides } from "./CoverMedia";
import { TeamBonusStatCard } from "./TeamBonusCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const abs = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

let catalogPromise = null;
const loadCatalog = () => {
  if (!catalogPromise) catalogPromise = axios.get(`${API}/product-images`).then((r) => r.data.items || []).catch(() => { catalogPromise = null; return []; });
  return catalogPromise;
};

const STOP = new Set(["vape", "vapes", "model", "modele", "puff", "puffs", "the", "de", "la", "le"]);
const toks = (x) => String(x || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim().split(" ").filter((w) => w && !STOP.has(w));
// Name -> best catalogue key by shared words (e.g. "Model Kang vape" ~ "Kang 15000", "olit hookahlit" ~ "Hookahlit 40K Olit").
export const matchProductName = (name, keys) => {
  const a = new Set(toks(name));
  if (!a.size) return null;
  let best = null, score = 0, tie = 0;
  keys.forEach((k) => {
    const b = [...new Set(toks(k))];
    if (!b.length) return;
    const shared = b.filter((w) => a.has(w));
    const common = shared.length;
    if (!shared.some((w) => /^[a-z]{3,}$/.test(w))) return;
    const sc = common / Math.min(a.size, b.length), t2 = common / Math.max(a.size, b.length);
    if (sc < 1 && common < 2) return;
    if (sc > score || (sc === score && t2 > tie)) { score = sc; tie = t2; best = k; }
  });
  return score >= 0.5 ? best : null;
};

// Catalogue photo for any product name (exact -> contains -> shared words).
export const imageForName = (name, items) => {
  const n = String(name || "").toLowerCase().trim();
  if (!n || !items?.length) return null;
  const list = items.filter((i) => i.image_url).map((i) => ({ ...i, k: (i.name_lc || i.name || "").toLowerCase().trim() })).filter((i) => i.k);
  const k = matchProductName(n, list.map((i) => i.k));
  const hit = list.find((i) => i.k === n) || list.find((i) => i.k.length > 2 && (n.includes(i.k) || i.k.includes(n))) || list.find((i) => i.k === k);
  return hit ? abs(hit.image_url) : null;
};

// Product catalogue (name + image + price) shared by every dashboard.
export const useProductCatalog = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { loadCatalog().then(setItems); }, []);
  const imageFor = useMemo(() => (name) => imageForName(name, items), [items]);
  return { items, imageFor };
};

export const ProductThumb = ({ name, size = 28 }) => {
  const { imageFor } = useProductCatalog();
  const src = imageFor(name);
  return src ? <img src={src} alt={name} style={{ width: size, height: size }} className="rounded-md object-contain bg-white border border-zinc-200 inline-block align-middle" /> : null;
};

// Sale input with instant product detection + auto-complete (image + name + price).
export const ProductAutocomplete = ({ value, onChange, onPick, models = [], testid, className, placeholder }) => {
  const { items, imageFor } = useProductCatalog();
  const [open, setOpen] = useState(false);
  const q = (value || "").toLowerCase().trim();
  const pool = useMemo(() => {
    const map = new Map();
    models.forEach((m) => m.name && map.set(m.name.toLowerCase(), { name: m.name, price: m.price, qty: m.quantity }));
    items.forEach((i) => !map.has(i.name_lc) && map.set(i.name_lc, { name: i.name, price: i.price }));
    return [...map.values()];
  }, [models, items]);
  const hits = q ? pool.filter((p) => p.name.toLowerCase().includes(q) || q.split(" ").every((w) => p.name.toLowerCase().includes(w))).slice(0, 6) : [];
  const exact = hits.length === 1 && hits[0].name.toLowerCase() === q;
  return (
    <div className="relative">
      <div className="relative">
        {imageFor(value) && <img src={imageFor(value)} alt="" className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded object-contain bg-white" />}
        <input type="text" value={value} placeholder={placeholder} autoComplete="off" data-testid={testid}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => { if ((e.key === "Enter" || e.key === "Tab") && hits[0] && !exact) { e.preventDefault(); onPick(hits[0]); setOpen(false); } }}
          className={`${className} ${imageFor(value) ? "pl-11" : ""}`} />
      </div>
      {open && hits.length > 0 && !exact && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden" data-testid={`${testid}-suggestions`}>
          {hits.map((h, i) => (
            <button type="button" key={h.name} onMouseDown={(e) => { e.preventDefault(); onPick(h); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-amber-50 ${i === 0 ? "bg-amber-50/60" : ""}`} data-testid={`${testid}-suggestion-${i}`}>
              {imageFor(h.name) ? <img src={imageFor(h.name)} alt="" className="w-9 h-9 rounded-md object-contain bg-white border border-zinc-100" /> : <div className="w-9 h-9 rounded-md bg-zinc-100 flex items-center justify-center">💨</div>}
              <span className="flex-1 text-zinc-900 font-bold text-sm">{h.name}</span>
              {h.price ? <span className="text-amber-700 text-xs font-black">${h.price}</span> : null}
              {h.qty !== undefined && <span className="text-zinc-500 text-[11px]">({h.qty})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ===== Stocker-identical visual blocks (used by Network Marketer dashboard) =====
export const CountryHero = ({ user, roleLabel, onViewStock }) => {
  const [cover, setCover] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dashboard-cover-for-country/${user?.country || "general"}`).then((r) => setCover(r.data?.cover || null)).catch(() => {});
  }, [user?.country]);
  return (
    <div className="relative rounded-2xl overflow-hidden mb-5 shadow-xl" style={{ minHeight: 220 }} data-testid="nm-welcome-cover" data-no-contrast-guard>
      {coverSlides(cover).length ? <CoverMedia cover={cover} /> : (
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-zinc-900">
          <div className="absolute right-6 top-6 opacity-20 text-white text-6xl font-black italic">Flavors</div>
          <div className="absolute right-6 bottom-6 opacity-20 text-amber-400 text-5xl font-black italic">Future</div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
      <div className="relative p-6 md:p-8 min-h-[220px] flex flex-col justify-center max-w-xl">
        <p className="text-white/90 text-sm md:text-base font-light leading-tight">Welcome Back,</p>
        <h1 className="text-white font-black text-3xl md:text-5xl leading-tight tracking-tight mt-0.5">{user.name}</h1>
        <p className="text-zinc-300 text-xs md:text-sm mt-1.5">{roleLabel} Code: <span className="font-mono font-bold text-amber-300">{user.code}</span></p>
        <p className="text-zinc-400 text-xs md:text-sm italic mt-1">{cover?.subtitle || "Keep pushing, great things ahead!"}</p>
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button onClick={onViewStock} className="bg-amber-400 hover:bg-amber-300 text-black text-xs md:text-sm font-black px-5 py-2 rounded-full shadow-lg active:scale-95 transition-transform" data-testid="nm-cover-view-stock-btn">View My Stock →</button>
          {cover?.title && <span className="bg-white/10 backdrop-blur border border-white/20 text-white font-black text-[10px] px-3 py-1.5 rounded-full uppercase tracking-widest">{cover.title}</span>}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ cls, icon, label, value, sub, labelCls, valueCls, subCls, testid }) => (
  <div className={`${cls} rounded-2xl p-4 shadow-sm hover:shadow-md transition`} data-testid={testid}>
    <div className="flex items-center justify-between mb-2">{icon}</div>
    <p className={`${labelCls} text-[11px] font-bold uppercase tracking-wider`}>{label}</p>
    <p className={`${valueCls} font-black text-3xl mt-0.5`}>{value}</p>
    <p className={`${subCls} text-[10px] font-semibold mt-0.5`}>{sub}</p>
  </div>
);

export const StatsRow = ({ sold, commission, rate, bonus, balance, userId }) => (
  <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5" data-testid="nm-stats-cards-row">
    <Stat testid="nm-stat-total-sold" cls="bg-gradient-to-br from-amber-50 to-amber-200 border border-amber-300" icon={<><div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-amber-700" /></div><BarChart3 className="w-4 h-4 text-amber-600/60" /></>} label="Total Sold" value={sold} sub="This Cycle" labelCls="text-amber-900" valueCls="text-amber-900" subCls="text-amber-700" />
    <Stat testid="nm-stat-commission" cls="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800" icon={<div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><Wallet className="w-5 h-5 text-amber-400" /></div>} label="Commission" value={`$${(commission || 0).toFixed(2)}`} sub={rate ? `${rate}%` : ""} labelCls="text-zinc-400" valueCls="text-white" subCls="text-amber-400" />
    <Stat testid="nm-stat-bonuses" cls="bg-gradient-to-br from-emerald-50 to-teal-100 border border-teal-200" icon={<div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center"><Gift className="w-5 h-5 text-teal-700" /></div>} label="Bonus" value={`$${(bonus || 0).toFixed(2)}`} sub="This Cycle" labelCls="text-teal-900" valueCls="text-teal-900" subCls="text-teal-700" />
    <TeamBonusStatCard userId={userId} testid="nm-stat-team-bonus" />
    <Stat testid="nm-stat-balance" cls="bg-gradient-to-br from-zinc-100 to-zinc-200 border border-zinc-300" icon={<div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center"><Package className="w-5 h-5 text-amber-400" /></div>} label="Balance" value={`$${(balance || 0).toFixed(2)}`} sub="Pending Payment" labelCls="text-zinc-700" valueCls="text-zinc-900" subCls="text-zinc-500" />
  </div>
);

export const MyStockVisual = ({ user }) => {
  const { imageFor } = useProductCatalog();
  const models = user.vape_models || [];
  if (!models.length) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-2xl p-4 md:p-5 shadow-sm mb-5" data-testid="nm-my-stock-visual">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-amber-400 rounded-xl flex items-center justify-center shadow"><Package className="w-5 h-5 text-black" /></div>
        <div>
          <h3 className="text-zinc-900 font-black text-base">My Stock</h3>
          <p className="text-zinc-500 text-[10px] uppercase tracking-widest font-black">{user.current_stock || 0} LEFT OUT OF {user.initial_stock || 0}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {models.map((m, i) => {
          const qty = parseInt(m.quantity || 0), sold = parseInt(m.sold || 0), left = Math.max(0, qty - sold);
          const pct = qty > 0 ? Math.min(100, Math.round((left / qty) * 100)) : 0;
          return (
            <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 hover:border-amber-400 hover:shadow-md transition" data-testid={`nm-my-stock-model-${i}`}>
              <div className="aspect-square bg-white rounded-lg overflow-hidden flex items-center justify-center mb-2 border border-zinc-100">
                {imageFor(m.name) ? <img src={imageFor(m.name)} alt={m.name} className="w-full h-full object-contain" data-testid={`nm-my-stock-img-${i}`} /> : <div className="text-5xl opacity-20">💨</div>}
              </div>
              <p className="text-zinc-900 font-black text-xs truncate" title={m.name}>{m.name}</p>
              <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden my-2"><div className={`h-full rounded-full ${pct > 50 ? "bg-amber-400" : pct > 20 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${pct}%` }} /></div>
              <div className="flex justify-between text-[11px]">
                <span className={`font-black ${left === 0 ? "text-red-600" : left <= 2 ? "text-amber-700" : "text-emerald-700"}`}>Left: {left}</span>
                <span className="text-zinc-500">Sold: {sold}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const useSiteConfig = () => {
  const [cfg, setCfg] = useState({});
  useEffect(() => { axios.get(`${API}/site-config`).then((r) => setCfg(r.data || {})).catch(() => {}); }, []);
  return cfg;
};

// "Latest Announcements" — image or video slides, auto-advance every 4s.
export const AnnouncementSlider = ({ items = [] }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);
  if (!items.length) return null;
  return (
    <div className="relative rounded-xl overflow-hidden border border-zinc-200 bg-black" style={{ minHeight: 170 }} data-testid="announcement-slider" data-no-contrast-guard>
      {items.map((a, i) => (
        <div key={a.id} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === idx ? 1 : 0, pointerEvents: i === idx ? "auto" : "none" }} data-testid={`announcement-${a.id}`}>
          {a.video_url ? <video src={abs(a.video_url)} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
            : a.image_url ? <img src={abs(a.image_url)} alt={a.title} className="absolute inset-0 w-full h-full object-cover" /> : null}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />
          <div className="relative p-4 h-full min-h-[170px] flex flex-col justify-center">
            <h4 className="text-amber-400 font-black text-lg tracking-wide leading-tight">{a.title}</h4>
            {a.body && <p className="text-white text-xs mt-0.5 line-clamp-2">{a.body}</p>}
            {a.cta_label && (
              <button onClick={() => a.cta_link && window.open(a.cta_link, "_blank", "noopener")} className="mt-2 w-fit bg-amber-400 hover:bg-amber-300 text-black font-black text-[10px] px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg" data-testid={`announcement-cta-${a.id}`}>{a.cta_label} →</button>
            )}
          </div>
        </div>
      ))}
      {items.length > 1 && (
        <div className="absolute bottom-2 right-3 z-10 flex gap-1.5">
          {items.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-[width] ${i === idx ? "w-5 bg-amber-400" : "w-1.5 bg-white/60"}`} data-testid={`announcement-dot-${i}`} />)}
        </div>
      )}
    </div>
  );
};

// CEO settings: shows the catalogue photo of a model so the CEO can confirm it (same photo as on the stocker dashboard).
export const ModelPhotoConfirm = ({ name }) => {
  const { imageFor } = useProductCatalog();
  if (!name) return null;
  const src = imageFor(name);
  return src ? (
    <div className="flex items-center gap-2 mt-1" data-testid="model-photo-confirm">
      <img src={src} alt={name} className="w-12 h-12 object-contain bg-white rounded-lg border-2 border-emerald-500" />
      <span className="text-emerald-400 text-[10px] font-black">✓ Photo du modèle (visible chez le stocker)</span>
    </div>
  ) : <p className="text-amber-400 text-[10px] font-bold mt-1" data-testid="model-photo-missing">⚠ Pas de photo — ajoutez-la dans « Réceptions Stock › Catalogue »</p>;
};
